import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ArrowRight, Eye, EyeOff, Loader2, X, Mail, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";

export function AccountPromptModal({ forceShow, onDismissForce }: { forceShow?: boolean; onDismissForce?: () => void } = {}) {
  const {
    hasAccount,
    postsGeneratedCount,
    accountPromptDismissed,
    sessionId,
    displayName,
    loginUser,
    dismissAccountPrompt,
    setCapturedEmail,
  } = useAppStore();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const [step, setStep] = useState<"email" | "password">("email");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isSubscribed = useAppStore((s) => s.isSubscribed);
  const hardPaywallActive = postsGeneratedCount >= 15 && !isSubscribed;
  const autoShow = postsGeneratedCount >= 5 && postsGeneratedCount < 15 && !hasAccount && !accountPromptDismissed;
  const shouldShow = !hardPaywallActive && (forceShow || autoShow);

  useEffect(() => {
    if (shouldShow) {
      triggerRef.current = document.activeElement;
    } else if (triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [shouldShow]);

  useEffect(() => {
    if (!shouldShow) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    requestAnimationFrame(() => {
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute("disabled"));
      if (focusable.length > 0) focusable[0].focus();
    });
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleDismiss();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute("disabled"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shouldShow]);

  const handleDismiss = () => {
    dismissAccountPrompt();
    if (email.trim()) {
      setCapturedEmail(email.trim());
    }
    toast({
      title: "Posts saved locally",
      description: "Sign up to keep them safe across devices.",
    });
    if (onDismissForce) onDismissForce();
  };

  const handleGoToLogin = () => {
    dismissAccountPrompt();
    if (email.trim()) {
      setCapturedEmail(email.trim());
    }
    if (onDismissForce) onDismissForce();
    setLocation("/login");
  };

  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setCapturedEmail(email.trim());
    setStep("password");
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name: displayName, sessionId, username: username.trim() }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Signup failed");
        return;
      }

      loginUser({
        token: data.token,
        userId: data.user.id,
        email: data.user.email,
        emailVerified: data.user.emailVerified,
        canonicalSessionId: data.user.canonicalSessionId,
        displayName: data.user.displayName,
      });

      toast({
        title: "Account created!",
        description: "Check your email to verify your account.",
      });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center md:p-4">
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-prompt-title"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="w-full md:max-w-md bg-card border-t border-border md:border md:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden"
          >
            <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mt-3 mb-1 md:hidden" />

            <div className="p-6 md:p-8 space-y-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    {step === "email" ? <Mail className="w-5 h-5 text-primary" /> : <Shield className="w-5 h-5 text-primary" />}
                  </div>
                  <div>
                    <h2 id="account-prompt-title" className="font-display font-bold text-lg">
                      {step === "email" ? "Your posts deserve a home" : "Almost there!"}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {step === "email"
                        ? "Save your voice profile and history across devices."
                        : "Create a password to secure your account."}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDismiss}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 -mr-1 -mt-1"
                  aria-label="Close dialog"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                {step === "email" ? (
                  <motion.form
                    key="email-step"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onSubmit={handleEmailContinue}
                    className="space-y-3"
                  >
                    <Input
                      type="email"
                      placeholder="Your email"
                      aria-label="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 rounded-xl bg-background border-border"
                      autoComplete="email"
                      required
                      autoFocus
                    />

                    <Button
                      type="submit"
                      className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
                      disabled={!email.trim()}
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="password-step"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    onSubmit={handleSignup}
                    className="space-y-3"
                  >
                    {error && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl p-3"
                      >
                        {error}
                      </motion.p>
                    )}

                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary/50 border border-border text-sm text-muted-foreground">
                      <Mail className="w-4 h-4 shrink-0" />
                      <span className="truncate">{email}</span>
                      <button
                        type="button"
                        onClick={() => setStep("email")}
                        className="ml-auto text-primary text-xs hover:underline shrink-0"
                      >
                        Change
                      </button>
                    </div>

                    <Input
                      type="text"
                      placeholder="Choose a username (4+ chars)"
                      aria-label="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="h-11 rounded-xl bg-background border-border"
                      autoComplete="username"
                      minLength={4}
                      maxLength={30}
                      pattern="[a-zA-Z0-9_]+"
                      title="Letters, numbers, and underscores only"
                      required
                    />

                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password (8+ chars)"
                        aria-label="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-11 rounded-xl bg-background border-border pr-10"
                        autoComplete="new-password"
                        minLength={8}
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
                      disabled={loading || !email || password.length < 8}
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Create free account
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </motion.form>
                )}
              </AnimatePresence>

              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={handleGoToLogin}
                  className="w-full text-center text-sm text-primary hover:text-primary/80 font-medium transition-colors flex items-center justify-center gap-1.5"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Already have an account? Log in →
                </button>
                <button
                  onClick={handleDismiss}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
